import { useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { MIN_PATTERN_DOTS, patternKey, withPassedDots } from "@/utils/appLock";

const DOTS = Array.from({ length: 9 }, (_, index) => index);

function dotPercent(index) {
  return {
    x: ((index % 3) + 0.5) * (100 / 3),
    y: (Math.floor(index / 3) + 0.5) * (100 / 3),
  };
}

export default function PatternLock({ onComplete, onIncomplete, disabled = false, status = "idle", resetSignal = 0 }) {
  const boardRef = useRef(null);
  const indicesRef = useRef([]);
  const drawingRef = useRef(false);
  const [indices, setIndices] = useState([]);
  const [pointer, setPointer] = useState(null);
  const [signal, setSignal] = useState(resetSignal);

  if (signal !== resetSignal) {
    indicesRef.current = [];
    drawingRef.current = false;
    setSignal(resetSignal);
    setIndices([]);
    setPointer(null);
  }

  const strokeClass = status === "error" ? "stroke-destructive" : status === "success" ? "stroke-green-600" : "stroke-primary";
  const dotClass = status === "error" ? "border-destructive bg-destructive" : status === "success" ? "border-green-600 bg-green-600" : "border-primary bg-primary";

  function hitTest(clientX, clientY) {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    const cell = rect.width / 3;
    const reach = cell * 0.34;
    for (const index of DOTS) {
      const col = index % 3;
      const row = Math.floor(index / 3);
      const x = rect.left + ((col + 0.5) / 3) * rect.width;
      const y = rect.top + ((row + 0.5) / 3) * rect.height;
      const dx = clientX - x;
      const dy = clientY - y;
      if (dx * dx + dy * dy <= reach * reach) return index;
    }
    return null;
  }

  function addDot(index) {
    const next = withPassedDots(indicesRef.current, index);
    if (next === indicesRef.current) return;
    indicesRef.current = next;
    setIndices(next);
  }

  function finish() {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    setPointer(null);
    const pattern = patternKey(indicesRef.current);
    const count = indicesRef.current.length;
    if (count >= MIN_PATTERN_DOTS) onComplete?.(pattern);
    else if (count > 0) onIncomplete?.();
  }

  function onPointerDown(event) {
    if (disabled) return;
    const index = hitTest(event.clientX, event.clientY);
    if (index == null) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drawingRef.current = true;
    indicesRef.current = [];
    setIndices([]);
    addDot(index);
  }

  function onPointerMove(event) {
    if (!drawingRef.current || disabled || !boardRef.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    setPointer({
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100,
    });
    const index = hitTest(event.clientX, event.clientY);
    if (index != null) addDot(index);
  }

  const last = indices.length ? dotPercent(indices[indices.length - 1]) : null;

  return (
    <div
      ref={boardRef}
      dir="ltr"
      role="application"
      aria-label="الگوی قفل"
      className={cn(
        "relative mx-auto aspect-square w-full max-w-[280px] touch-none select-none",
        disabled && "pointer-events-none opacity-50"
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
    >
      <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
        {indices.slice(1).map((index, lineIndex) => {
          const from = dotPercent(indices[lineIndex]);
          const to = dotPercent(index);
          return (
            <line
              key={`${indices[lineIndex]}-${index}`}
              x1={`${from.x}%`}
              y1={`${from.y}%`}
              x2={`${to.x}%`}
              y2={`${to.y}%`}
              className={strokeClass}
              strokeWidth="4"
              strokeLinecap="round"
            />
          );
        })}
        {last && pointer ? (
          <line
            x1={`${last.x}%`}
            y1={`${last.y}%`}
            x2={`${pointer.x}%`}
            y2={`${pointer.y}%`}
            className={cn(strokeClass, "opacity-70")}
            strokeWidth="4"
            strokeLinecap="round"
          />
        ) : null}
      </svg>
      {DOTS.map((index) => {
        const point = dotPercent(index);
        const selected = indices.includes(index);
        return (
          <span
            key={index}
            className={cn(
              "absolute flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full",
              selected ? "bg-primary/10" : "bg-transparent"
            )}
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
          >
            <span
              className={cn(
                "h-4 w-4 rounded-full border-2 transition-transform",
                selected ? cn(dotClass, "scale-125") : "border-muted-foreground/45 bg-background"
              )}
            />
          </span>
        );
      })}
    </div>
  );
}
